// src/pages/HomePage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase/firebase-config";
import { collection, getDocs, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { Button, Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { FaSignOutAlt, FaPlus } from 'react-icons/fa';
import AddSystemForm from '../components/AddSystemForm';
import './HomePage.css';

function HomePage() {
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const navigate = useNavigate();
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch systems from Firebase
  useEffect(() => {
    const fetchSystems = async () => {
      try {
        setLoading(true);
        const systemsCollection = collection(db, 'systems');
        const systemsQuery = query(systemsCollection, orderBy('createdAt', 'desc'));
        const systemsSnapshot = await getDocs(systemsQuery);
        
        const systemsData = systemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamp to Date if needed
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        }));
        
        setSystems(systemsData);
        setError(null);
      } catch (err) {
        console.error("Error fetching systems:", err);
        setError("Failed to load systems. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, []);

  // Filter systems based on search term
  const filteredSystems = systems.filter(system =>
    system.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    system.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    system.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
    system.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Clear localStorage
      localStorage.removeItem("uid");
      localStorage.removeItem("isAdmin");
      // Redirect to login page
      navigate("/login");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleImageClick = (system) => {
    if (system.siteLink) {
      window.open(system.siteLink, '_blank');
    }
  };

  const handleEdit = (system) => {
    console.log("Edit", system.title);
    navigate(`/edit/${system.id}`);
  };

  const handleDelete = async (system) => {
    if (window.confirm(`Are you sure you want to delete "${system.title}"?`)) {
      try {
        await deleteDoc(doc(db, 'systems', system.id));
        // Remove from local state
        setSystems(prevSystems => prevSystems.filter(s => s.id !== system.id));
        console.log("System deleted successfully");
      } catch (error) {
        console.error("Error deleting system:", error);
        alert("Failed to delete system. Please try again.");
      }
    }
  };

  const handleGithub = (system) => {
    console.log("GitHub", system.title);
    if (system.githubLink) {
      window.open(system.githubLink, '_blank');
    } else {
      alert("No GitHub link available for this system.");
    }
  };

  const formatRating = (rating) => {
    if (!rating) return "No rating";
    return "⭐".repeat(Math.floor(rating)) + ` (${rating}/5)`;
  };

  const formatDate = (date) => {
    // Handle null/undefined
    if (!date) return "No date";
    
    // Try to convert to Date if it's not already
    let dateObj = date;
    if (!(date instanceof Date)) {
      try {
        // Handle Firebase timestamp
        if (date.seconds) {
          dateObj = new Date(date.seconds * 1000);
        } 
        // Handle string date
        else if (typeof date === 'string') {
          dateObj = new Date(date);
        }
        // Handle number (timestamp)
        else if (typeof date === 'number') {
          dateObj = new Date(date);
        }
      } catch (e) {
        console.error("Error converting to date:", e);
        return "Invalid date";
      }
    }
    
    // Check if the date is valid
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return "Invalid date";
    }
    
    // Format the date
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(dateObj);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const handleSystemAdded = (newSystem) => {
    // Add the new system to the local state with safe timestamp handling
    const processedSystem = {
      ...newSystem,
    };
    
    // Handle timestamps in a more robust way
    if (newSystem.createdAt) {
      // Check if it's a Firebase timestamp with seconds property
      if (newSystem.createdAt.seconds) {
        processedSystem.createdAt = new Date(newSystem.createdAt.seconds * 1000);
      } 
      // Check if it's a standard Date object
      else if (newSystem.createdAt instanceof Date) {
        processedSystem.createdAt = newSystem.createdAt;
      }
      // Otherwise, use current date as fallback
      else {
        processedSystem.createdAt = new Date();
      }
    } else {
      processedSystem.createdAt = new Date();
    }
    
    // Similar handling for updatedAt
    if (newSystem.updatedAt) {
      if (newSystem.updatedAt.seconds) {
        processedSystem.updatedAt = new Date(newSystem.updatedAt.seconds * 1000);
      } else if (newSystem.updatedAt instanceof Date) {
        processedSystem.updatedAt = newSystem.updatedAt;
      } else {
        processedSystem.updatedAt = new Date();
      }
    } else {
      processedSystem.updatedAt = new Date();
    }
    
    // Update state with the processed system
    setSystems(prevSystems => [processedSystem, ...prevSystems]);
    
    // Switch back to dashboard view
    setActiveMenuItem('dashboard');
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h4> System Manager</h4>
        </div>
        
        <nav className="sidebar-nav">
          <div 
            className={`sidebar-item ${activeMenuItem === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveMenuItem('dashboard')}
          >
            <span className="sidebar-icon">🏠</span>
            Dashboard
          </div>
          <div 
            className={`sidebar-item ${activeMenuItem === 'add-system' ? 'active' : ''}`}
            onClick={() => setActiveMenuItem('add-system')}
          >
            <span className="sidebar-icon">
              <FaPlus style={{ color: 'white' }} />
            </span>
            Add New System
          </div>
        </nav>

        <div className="sidebar-footer">
          <Button 
            variant="danger" 
            onClick={handleSignOut}
            className="w-100 signout-btn"
            size="sm"
          >
            <FaSignOutAlt className="me-2" />Sign Out
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <div className="search-container">
            <div className="search-bar">
              <div className="search-input-wrapper">
                <input 
                  type="text" 
                  placeholder="Search by title, description, tags, or author..." 
                  className="form-control search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="search-icon">🔍</span>
              </div>
              <Button 
                variant="primary" 
                className="search-btn"
                disabled={searchTerm.trim().length === 0}
              >
                Search {filteredSystems.length > 0 && `(${filteredSystems.length})`}
              </Button>
            </div>
            {searchTerm.trim().length > 0 && (
              <div className="search-tags">
                <span className="search-tag">
                  {searchTerm}
                  <button 
                    className="clear-search" 
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="content-body">
          {activeMenuItem === 'dashboard' && (
            <>
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-3">Loading systems...</p>
                </div>
              ) : filteredSystems.length === 0 ? (
                <div className="text-center py-5">
                  <h5>No systems found</h5>
                  <p className="text-muted">
                    {searchTerm ? 'Try adjusting your search terms.' : 'No systems have been added yet.'}
                  </p>
                </div>
              ) : (
                <Row className="g-4">
                  {filteredSystems.map((system) => (
                    <Col xl={3} lg={4} md={6} sm={12} key={system.id}>
                      <Card className="system-card h-100">
                        <div className="card-image-container">
                          <div 
                            className="card-image-clickable" 
                            onClick={() => handleImageClick(system)}
                            style={{ cursor: system.siteLink ? 'pointer' : 'default' }}
                            title={system.siteLink ? 'Click to visit website' : 'No website available'}
                          >
                            <Card.Img 
                              variant="top" 
                              src={system.imageUrl || 'https://via.placeholder.com/300x180?text=No+Image'} 
                              alt={system.title}
                              className="card-image"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/300x180?text=Image+Not+Found';
                              }}
                            />
                            {system.siteLink && (
                              <div className="card-visit-overlay">
                                <span>Click to Visit Website</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="system-title">{system.title}</Card.Title>
                          
                          <div className="system-meta">
                            <small className="text-muted">
                              <strong>Rating:</strong> {"⭐".repeat(system.rating || 0)}<br/>
                              <strong>Created:</strong> {formatDate(system.createdAt)}<br/>
                              {system.tags && system.tags.length > 0 && (
                                <>
                                  <strong>Tags:</strong> {system.tags.join(', ')}
                                </>
                              )}
                            </small>
                          </div>
                          
                          <div className="card-actions mt-auto">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleEdit(system)}
                              className="action-btn"
                              title="Edit System"
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleDelete(system)}
                              className="action-btn"
                              disabled={!isAdmin}
                              title={isAdmin ? 'Delete System' : 'Admin access required'}
                            >
                              Delete
                            </Button>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleGithub(system)}
                              className="action-btn"
                              disabled={!system.githubLink}
                              title={system.githubLink ? 'View on GitHub' : 'No GitHub link available'}
                            >
                              GitHub
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}

          {activeMenuItem === 'add-system' && (
            <div className="add-system-content">
              <AddSystemForm onSystemAdded={handleSystemAdded} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
